import { useEffect, useMemo, useState } from 'react';
import { ExternalLink, Loader2, RotateCw, X } from 'lucide-react';
import type { OfferTemplate, OfferTemplateContent } from '@/features/webinar/types';
import type { WorkspaceVisualIdentity } from '@/features/workspace/types';
import { useWebinarT } from '@/hooks/useWebinarT';
import {
  OFFER_EMBED_IFRAME_ALLOW,
  OFFER_EMBED_IFRAME_SANDBOX,
  buildWhopCheckoutFrameSrc,
  isLightOfferPanel,
  isStripeCheckoutOffer,
  resolveOfferEmbed,
  resolveOfferEmbedFallbackUrl,
} from '@/lib/offerEmbed';
import { cn } from '@/lib/utils';
import StripeCheckoutFrame, {
  type StripeCheckoutAccess,
} from '@/components/pages/live/attendee/StripeCheckoutFrame';

// Au-delà, on considère que le service ne répond pas et on propose de réessayer.
const EMBED_LOAD_TIMEOUT_MS = 15000;

// « loaded » veut seulement dire que l'iframe a fini de charger : un service qui refuse d'être
// intégré déclenche aussi onLoad. Le lien d'ouverture dans un onglet reste donc affiché quel que
// soit l'état, dès qu'une URL sûre existe (lien de repli, ou URL de l'iframe du service).
type EmbedStatus = 'loading' | 'loaded' | 'error';

interface OfferEmbedPanelProps {
  offer: OfferTemplate;
  visualIdentity: WorkspaceVisualIdentity | null | undefined;
  onClose: () => void;
  // Paiement Stripe uniquement : sans accès (Evergreen, jeton absent), il est indiqué indisponible.
  stripeCheckout?: StripeCheckoutAccess | null;
  className?: string;
}

export default function OfferEmbedPanel({
  offer,
  visualIdentity,
  onClose,
  stripeCheckout = null,
  className,
}: OfferEmbedPanelProps) {
  const { t } = useWebinarT('webinars');
  const content = offer.contents?.[0];
  const isLightTheme = isLightOfferPanel(visualIdentity);

  return (
    <div className={cn('h-full min-h-0 flex flex-col gap-2 p-2', className)}>
      <div className="flex items-center justify-between gap-2 pl-1">
        <p
          className={cn(
            'truncate text-sm font-semibold',
            isLightTheme ? 'text-[#1f1f1f]' : 'text-white',
          )}
        >
          {content?.title}
        </p>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('live.offerEmbed.close')}
          title={t('live.offerEmbed.close')}
          className={cn(
            'shrink-0 rounded-full p-2 transition-colors cursor-pointer',
            isLightTheme
              ? 'text-[#1f1f1f] hover:bg-black/10'
              : 'text-white bg-white/10 hover:bg-white/20',
          )}
        >
          <X className="size-4" aria-hidden />
        </button>
      </div>

      {isStripeCheckoutOffer(content) ? (
        <StripeCheckoutFrame
          offerId={offer.id}
          access={stripeCheckout}
          priceOptions={content?.stripePrices}
          isLightTheme={isLightTheme}
          buttonColor={content?.buttonColor}
          buttonTextColor={content?.buttonTextColor}
        />
      ) : (
        <ProviderEmbedFrame content={content} isLightTheme={isLightTheme} />
      )}
    </div>
  );
}

function ProviderEmbedFrame({
  content,
  isLightTheme,
}: {
  content: OfferTemplateContent | undefined;
  isLightTheme: boolean;
}) {
  const { t } = useWebinarT('webinars');
  const render = useMemo(() => resolveOfferEmbed(content, window.location.host), [content]);
  const frameSrc =
    render?.kind === 'iframe'
      ? render.src
      : render?.kind === 'whop-checkout'
        ? buildWhopCheckoutFrameSrc(
            render.checkoutPlanId,
            window.location.origin,
            isLightTheme ? 'light' : 'dark',
          )
        : null;
  const fallbackUrl =
    resolveOfferEmbedFallbackUrl(content) ?? (render?.kind === 'iframe' ? render.src : null);
  const [status, setStatus] = useState<EmbedStatus>(render ? 'loading' : 'error');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (status !== 'loading') return;
    const timer = window.setTimeout(() => setStatus('error'), EMBED_LOAD_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [status]);

  const handleRetry = () => {
    setAttempt((previous) => previous + 1);
    setStatus('loading');
  };

  return (
    <>
      <div className="relative flex-1 min-h-0 overflow-hidden rounded-2xl bg-white">
        {/* Reste monté après le délai : s'il finit par charger, le message d'erreur disparaît. */}
        {frameSrc && (
          <iframe
            key={attempt}
            src={frameSrc}
            title={content?.title || content?.embedProvider || ''}
            sandbox={OFFER_EMBED_IFRAME_SANDBOX}
            allow={OFFER_EMBED_IFRAME_ALLOW}
            referrerPolicy="strict-origin-when-cross-origin"
            className="absolute inset-0 size-full border-0"
            onLoad={() => setStatus('loaded')}
          />
        )}

        {status === 'loading' && (
          <div className="absolute inset-0 flex items-center justify-center bg-white text-[#1f1f1f]">
            <Loader2 className="size-6 animate-spin" aria-label={t('live.offerEmbed.loading')} />
          </div>
        )}
        {status === 'error' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-white p-6 text-center text-sm text-[#1f1f1f]">
            <p>{t('live.offerEmbed.unavailable')}</p>
            {render && (
              <button
                type="button"
                onClick={handleRetry}
                className="flex items-center gap-1 rounded-full border border-[#1f1f1f]/20 px-3 py-1 text-xs font-medium cursor-pointer hover:bg-black/5"
              >
                <RotateCw className="size-3" aria-hidden />
                {t('live.offerEmbed.retry')}
              </button>
            )}
          </div>
        )}
      </div>

      {fallbackUrl && (
        <a
          href={fallbackUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={cn(
            'flex items-center justify-center gap-1 text-xs underline-offset-2 hover:underline',
            isLightTheme ? 'text-[#1f1f1f]/70' : 'text-white/70',
          )}
        >
          <ExternalLink className="size-3" aria-hidden />
          {t('live.offerEmbed.openInNewTab')}
        </a>
      )}
    </>
  );
}
